import { ArrowUpTrayIcon } from '@heroicons/react/24/outline';
import React, { useRef } from 'react';

import { useFileDragDrop } from '../hooks/useFileDragDrop';

interface FileDragDropZoneProps {
  onFilesAdded: (files: FileList) => void;
  disabled?: boolean;
  children: React.ReactNode;
  footer?: React.ReactNode;
  accept?: string;
  multiple?: boolean;
}

const FileDragDropZone: React.FC<FileDragDropZoneProps> = ({
  onFilesAdded,
  disabled = false,
  children,
  footer,
  accept = 'application/pdf',
  multiple = true,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { isDragActive, handleDrop, handleDragOver, handleDragLeave } =
    useFileDragDrop({
      onFilesAdded,
      disabled,
    });

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      onFilesAdded(e.target.files);
    }
  };

  const handleFooterClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  return (
    <div
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      className={`relative bg-white dark:bg-gray-800 rounded-lg border-2 transition-colors duration-200 p-6 ${
        isDragActive
          ? 'border-blue-500 border-dashed bg-blue-50 dark:bg-blue-900/10'
          : 'border-gray-200 dark:border-gray-700'
      }`}
    >
      {/* Drag Overlay */}
      {isDragActive && (
        <div className='pointer-events-none absolute inset-0 flex items-center justify-center bg-blue-50/80 dark:bg-blue-900/20 rounded-lg z-10'>
          <div className='text-center'>
            <ArrowUpTrayIcon className='w-12 h-12 text-blue-600 dark:text-blue-400 mx-auto mb-2' />
            <p className='text-lg font-medium text-blue-600 dark:text-blue-400'>
              Drop files to add receipts
            </p>
          </div>
        </div>
      )}

      {/* Content */}
      {children}

      {/* Footer */}
      {footer && (
        <div
          role='button'
          tabIndex={disabled ? -1 : 0}
          onClick={handleFooterClick}
          onKeyDown={e => {
            if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              handleFooterClick();
            }
          }}
          className={`mt-4 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
        >
          {footer}
        </div>
      )}

      {/* Hidden File Input */}
      <input
        ref={fileInputRef}
        type='file'
        multiple={multiple}
        accept={accept}
        onChange={handleFileInputChange}
        className='hidden'
        disabled={disabled}
      />
    </div>
  );
};

export default FileDragDropZone;
